import { firstValueFrom } from 'rxjs'
import { map } from 'rxjs/operators'

import { Injectable } from '@angular/core'
import { FormControl } from '@angular/forms'

import { FixedSelectionModel } from '@app-lib/fixed-selection-model'
import { ShoppingCartLineFragment } from '@app-graphql/schema'
import { CheckoutService } from '@app-services'

@Injectable({
    providedIn: 'root',
})
export class CartSelectionService {

    public selectedProductsControl = new FormControl<ShoppingCartLineFragment[]>([])

    public selectAllControl = new FormControl(false, { nonNullable: true })

    public selectedRowsSelectionModel = new FixedSelectionModel<ShoppingCartLineFragment>(
        true,
        [],
        true,
        (p1, p2) => p1.id === p2.id,
    )

    constructor(
        private checkoutService: CheckoutService,
    ) {
        // Keep the selection in sync with the cart, so totals never include deleted lines or stale subtotals
        this.checkoutService.shoppingCart$.subscribe((cart) => {
            this.syncSelectionWithCartLines(cart.cartLine ?? [])
        })
    }

    public toggleRowInSelection(elements: ShoppingCartLineFragment[]): void {
        this.selectedRowsSelectionModel.clear()
        elements.forEach((element) => {
            this.selectedRowsSelectionModel.toggle(element)
        })

        this.setSelectedItemsSubject()
    }

    public handleDeselectAll(): void {
        this.selectedRowsSelectionModel.clear()
        this.selectAllControl.setValue(false, { emitEvent: false })
        this.selectedProductsControl.setValue([])
        this.setSelectedItemsSubject()
    }

    public async handleSelectAll(): Promise<void> {
        const cartlines = await firstValueFrom(
            this.checkoutService.shoppingCart$.pipe(
                map((r) => r.cartLine),
            ),
        )

        this.selectedRowsSelectionModel.setSelection(...cartlines)
        this.selectedProductsControl.setValue(cartlines)
        this.selectAllControl.setValue(true, { emitEvent: false })
        this.setSelectedItemsSubject()
    }

    public setSelectedItemsSubject(): void {
        this.checkoutService.selectedItemsInShoppingCart$.next(this.selectedRowsSelectionModel.selected)
    }

    private syncSelectionWithCartLines(cartLines: ShoppingCartLineFragment[]): void {
        if (! this.selectedRowsSelectionModel.hasValue()) {
            return
        }

        const selectedLines = cartLines.filter((line) => this.selectedRowsSelectionModel.selected
            .some((selected) => selected.id === line.id))

        if (selectedLines.length === 0) {
            this.handleDeselectAll()
            return
        }

        // clear() first, setSelection() would keep the stale line objects as they match by id
        this.selectedRowsSelectionModel.clear()
        this.selectedRowsSelectionModel.select(...selectedLines)
        this.selectedProductsControl.setValue(selectedLines, { emitEvent: false })
        this.selectAllControl.setValue(selectedLines.length === cartLines.length, { emitEvent: false })
        this.setSelectedItemsSubject()
    }

}
